import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Layout, Button, SectionLabel, SectionHeading } from "../components.jsx";
import {
  navy, navyMid, blue,
  STATS, INTEGRATIONS, INTEGRATION_LOGOS, RESULTS, STEPS, HOW_WE_HELP,
  SERVICES, INDUSTRIES, CASE_STUDIES,
} from "../data";

const CALENDLY_URL = "https://calendly.com/pankajp1/30min";

function useCalendlyScript() {
  useEffect(() => {
    // Load Calendly widget script once
    if (!document.getElementById("calendly-script")) {
      const script = document.createElement("script");
      script.id = "calendly-script";
      script.src = "https://assets.calendly.com/assets/external/widget.js";
      script.async = true;
      document.body.appendChild(script);
    }
    // Load Calendly CSS once
    if (!document.getElementById("calendly-css")) {
      const link = document.createElement("link");
      link.id = "calendly-css";
      link.rel = "stylesheet";
      link.href = "https://assets.calendly.com/assets/external/widget.css";
      document.head.appendChild(link);
    }
  }, []);
}

function openCalendlyPopup() {
  if (window.Calendly) {
    window.Calendly.initPopupWidget({ url: CALENDLY_URL });
  }
}

export default function Home() {
  const navigate = useNavigate();
  useCalendlyScript();

  return (
    <Layout>

      {/* ── HERO ────────────────────────────────────────────────────────────── */}
      <section
        className="relative min-h-screen flex items-center pt-20"
        style={{ background: `linear-gradient(135deg, ${navy} 0%, #1a3260 60%, #0e2251 100%)` }}
      >
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: "linear-gradient(rgba(255,255,255,0.5) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.5) 1px,transparent 1px)",
          backgroundSize: "60px 60px",
        }} />
        <div className="absolute top-1/3 right-1/4 w-96 h-96 rounded-full opacity-10 blur-3xl pointer-events-none" style={{ background: blue }} />

        <div className="relative max-w-7xl mx-auto px-6 py-24 md:py-32">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-full border border-blue-500/30 bg-blue-600/10">
              <div className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
              <span className="text-blue-300 text-xs font-semibold tracking-widest uppercase">AI Automation & Operational Intelligence</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-white leading-tight mb-6">
              Turn Your Operations Into{" "}
              <span style={{ color: "#60a5fa" }}>Automated, Scalable</span>{" "}
              Business Systems
            </h1>
            <p className="text-gray-300 text-lg md:text-xl leading-relaxed mb-6 max-w-2xl">
              VedaLogic helps business owners automate repetitive operations, respond to leads faster, and improve overall business efficiency — with practical AI systems that deliver measurable outcomes.
            </p>
            <ul className="mb-10 space-y-2">
              {["Automate repetitive workflows","Respond to leads faster","Turn manual operations into scalable systems"].map(item => (
                <li key={item} className="flex items-center gap-3 text-blue-200 text-sm font-medium">
                  <span style={{ color: "#60a5fa" }}>✓</span>{item}
                </li>
              ))}
            </ul>
            <p className="text-blue-300 text-sm font-medium mb-10 tracking-wide">Practical implementation. Real outcomes. No hype.</p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button onClick={openCalendlyPopup}>Book a Free Strategy Call</Button>
              <Button variant="secondary" onClick={() => navigate("/resources")}>Get My Free AI Roadmap</Button>
            </div>
          </div>
        </div>
      </section>

      {/* ── INTEGRATIONS SECTION ────────────────────────────────────────────── */}
      <section style={{ background: navyMid }} className="py-16 border-b border-white/10">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <SectionLabel>AI Systems Built to Work With Your Existing Tools</SectionLabel>
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4 leading-tight">
            AI That Fits Into the Tools You Already Use
          </h2>
          <p className="text-gray-400 text-base leading-relaxed mb-10 max-w-2xl mx-auto">
            We integrate AI into the platforms your business already relies on — so automation fits into your existing workflows rather than replacing your systems.
          </p>
          <div className="flex flex-wrap justify-center items-center gap-4 md:gap-5">
 {INTEGRATION_LOGOS.map((logo) => (
  <div
    key={logo.name}
    className="flex flex-col items-center justify-center rounded-lg transition-all duration-200 hover:scale-105"
    style={{
      background: "#ffffff",
      padding: "12px 18px",
      height: 64,
      minWidth: 110,
      boxShadow: "0 2px 12px rgba(0,0,0,0.25)",
      gap: 6,
    }}
  >
    <img
      src={`https://cdn.simpleicons.org/${logo.slug}${logo.color ? `/${logo.color}` : ""}`}
      alt={logo.name}
      style={{ height: 24, width: 24, objectFit: "contain" }}
    />
    <span style={{ color: "#374151", fontWeight: 600, fontSize: 11 }}>{logo.name}</span>
  </div>
))}
          </div>
        </div>
      </section>

      {/* ── STATS BAR ───────────────────────────────────────────────────────── */}
      <section style={{ background: navy }} className="py-12 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {STATS.map(s => (
              <div key={s.label} className="text-center">
                <div className="text-3xl md:text-4xl font-bold mb-1" style={{ color: "#60a5fa" }}>{s.value}</div>
                <div className="text-gray-400 text-sm font-medium">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW WE DELIVER ──────────────────────────────────────────────────── */}
      <section className="py-24" style={{ background: navy }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <SectionLabel>Our Process</SectionLabel>
            <SectionHeading light>How We Deliver AI Results</SectionHeading>
            <p className="text-gray-400 mt-4 max-w-xl mx-auto text-base">A structured, proven framework — from identifying opportunities to deploying systems that run without you.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {STEPS.map((step, i) => (
              <div key={step.num} className="relative rounded-lg p-7 border border-white/10 hover:border-blue-500/50 transition-all duration-200" style={{ background: "rgba(255,255,255,0.04)" }}>
                <div className="text-5xl font-bold mb-4 opacity-20 text-white">{step.num}</div>
                <div className="w-8 h-0.5 mb-4" style={{ background: blue }} />
                <h3 className="text-white font-bold text-base mb-3 leading-snug">{step.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{step.desc}</p>
                {i < STEPS.length - 1 && <div className="hidden lg:block absolute top-1/2 -right-3 text-blue-500 text-lg">→</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW WE HELP ─────────────────────────────────────────────────────── */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <SectionLabel>Our Approach</SectionLabel>
            <SectionHeading>How We Help Businesses Use AI</SectionHeading>
            <p className="text-gray-500 mt-4 max-w-xl mx-auto text-base">Whether you're exploring AI for the first time or ready to scale across your operations, we have a structured approach to fit where you are.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {HOW_WE_HELP.map((block, i) => (
              <div key={block.title} className="rounded-lg p-8 border transition-all duration-200 hover:shadow-lg" style={{ background: i === 1 ? navy : "#fff", borderColor: i === 1 ? "rgba(255,255,255,0.1)" : "#f3f4f6" }}>
                <div className="text-3xl mb-5">{block.icon}</div>
                <h3 className="font-bold text-lg mb-3 leading-snug" style={{ color: i === 1 ? "#fff" : navy }}>{block.title}</h3>
                <p className="text-sm mb-5 leading-relaxed" style={{ color: i === 1 ? "#9ca3af" : "#6b7280" }}>{block.desc}</p>
                <ul className="space-y-2">
                  {block.items.map(item => (
                    <li key={item} className="flex items-start gap-2">
                      <span style={{ color: "#60a5fa" }} className="mt-0.5 flex-shrink-0">▸</span>
                      <span className="text-sm" style={{ color: i === 1 ? "#d1d5db" : "#374151" }}>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SERVICES PREVIEW (3 cards) ───────────────────────────────────────── */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <SectionLabel>What We Do</SectionLabel>
            <SectionHeading>AI Services Focused on Business Outcomes</SectionHeading>
            <p className="text-gray-500 mt-4 max-w-xl mx-auto text-base">We don't just build AI tools — we solve operational problems with practical, deployable systems.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
            {SERVICES.slice(0, 3).map(s => (
              <div key={s.title} className="bg-white rounded-lg p-7 border border-gray-100 hover:border-blue-200 hover:shadow-md transition-all duration-200 group">
                <div className="text-3xl mb-5">{s.icon}</div>
                <h3 className="font-bold text-base mb-3 leading-snug group-hover:text-blue-600 transition-colors" style={{ color: navy }}>{s.title}</h3>
                <p className="text-gray-400 text-sm mb-3 leading-relaxed"><span className="font-semibold text-gray-500">Problem: </span>{s.problem}</p>
                <p className="text-blue-600 text-sm font-semibold leading-relaxed">✓ {s.outcome}</p>
              </div>
            ))}
          </div>
          <div className="text-center">
            <Button variant="outline" onClick={() => navigate("/services")}>View All Services →</Button>
          </div>
        </div>
      </section>

      {/* ── INDUSTRIES PREVIEW (3 cards) ────────────────────────────────────── */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <SectionLabel>Industries We Serve</SectionLabel>
            <SectionHeading>Built for Operations-Heavy Businesses</SectionHeading>
            <p className="text-gray-500 mt-4 max-w-xl mx-auto text-base">We focus on service businesses so we understand your workflows, challenges, and what AI can realistically deliver.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
            {INDUSTRIES.slice(0, 3).map(ind => (
              <div key={ind.name} className="bg-white rounded-lg overflow-hidden border border-gray-100 hover:shadow-lg transition-shadow duration-200">
                <div className="p-5 border-b border-gray-100" style={{ background: navy }}>
                  <div className="text-2xl mb-2">{ind.icon}</div>
                  <h3 className="text-white font-bold text-base">{ind.name}</h3>
                  <p className="text-blue-300 text-xs mt-1 font-medium">{ind.value}</p>
                </div>
                <div className="p-5">
                  <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-2">What We Automate</p>
                  {ind.automation.map(a => <p key={a} className="text-blue-600 text-sm mb-1 font-medium">✓ {a}</p>)}
                </div>
              </div>
            ))}
          </div>
          <div className="text-center">
            <Button variant="outline" onClick={() => navigate("/industries")}>View All Industries →</Button>
          </div>
        </div>
      </section>

      {/* ── ASSESSMENT TEASER ───────────────────────────────────────────────── */}
      <section className="py-24" style={{ background: `linear-gradient(135deg, ${navy} 0%, #1a3260 100%)` }}>
        <div className="max-w-4xl mx-auto px-6 text-center">
          <SectionLabel>Start Here</SectionLabel>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 leading-tight">Start With an AI Opportunity Assessment</h2>
          <p className="text-gray-300 text-base mb-4 max-w-2xl mx-auto leading-relaxed">
            The best first step is understanding exactly where AI creates value in your business. Our Assessment gives you a clear, structured picture — before committing to a full implementation.
          </p>
          <p className="text-blue-300 text-sm font-semibold mb-10">Most businesses uncover 3–5 automation opportunities that can save significant time and streamline operations.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={() => navigate("/ai-assessment")}>Learn About the Assessment</Button>
            <Button variant="secondary" onClick={openCalendlyPopup}>Book a Strategy Call</Button>
          </div>
        </div>
      </section>

      {/* ── CASE STUDIES ────────────────────────────────────────────────────── */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <SectionLabel>Client Results</SectionLabel>
            <SectionHeading>What Results Look Like in Practice</SectionHeading>
            <p className="text-gray-500 mt-4 max-w-xl mx-auto text-base">Illustrative examples of the outcomes businesses like yours achieve when AI is implemented properly.</p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {CASE_STUDIES.map(cs => (
              <div key={cs.type} className="bg-white rounded-lg overflow-hidden border border-gray-100 hover:shadow-lg transition-shadow duration-200">
                <div className="px-7 py-5 border-b border-gray-100" style={{ background: "#f8faff" }}>
                  <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: blue }}>Client Type</p>
                  <h3 className="font-bold text-base" style={{ color: navy }}>{cs.type}</h3>
                </div>
                <div className="p-7 space-y-4">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-1">Challenge</p>
                    <p className="text-gray-600 text-sm leading-relaxed">{cs.challenge}</p>
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-1">Solution</p>
                    <p className="text-gray-600 text-sm leading-relaxed">{cs.solution}</p>
                  </div>
                  <div className="pt-3 border-t border-gray-100">
                    <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-2">Results</p>
                    {cs.results.map(r => <p key={r} className="text-blue-600 text-sm font-semibold mb-1">✓ {r}</p>)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHO WE'RE BUILT FOR ─────────────────────────────────────────────── */}
      <section className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-14">
            <SectionLabel>Client Fit</SectionLabel>
            <SectionHeading>Who VedaLogic Is Built For</SectionHeading>
            <p className="text-gray-500 mt-4 max-w-xl mx-auto text-base">
              We work with a focused group of businesses so we can deliver real operational results — not just AI experiments.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="rounded-lg p-8 border-2 border-green-100 bg-green-50">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-600 font-bold text-sm">✓</div>
                <h3 className="font-bold text-lg text-green-800">Who We Work Best With</h3>
              </div>
              <div className="space-y-3">
                {[
                  "Service businesses with repetitive operational workflows",
                  "Companies handling significant customer enquiry volume",
                  "Teams spending hours on manual scheduling, follow-ups, or reporting",
                  "Business owners looking to improve efficiency without increasing headcount",
                  "Organisations with 10+ employees and defined operational processes",
                ].map(item => (
                  <div key={item} className="flex items-start gap-3">
                    <span className="text-green-500 flex-shrink-0 mt-0.5 font-bold">✓</span>
                    <p className="text-green-800 text-sm leading-relaxed">{item}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-lg p-8 border-2 border-red-100 bg-red-50">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center text-red-500 font-bold text-sm">✕</div>
                <h3 className="font-bold text-lg text-red-800">Who We're Probably Not the Right Fit For</h3>
              </div>
              <div className="space-y-3">
                {[
                  "Very early-stage startups with no established processes",
                  "Businesses only experimenting with AI tools without implementation intent",
                  "Companies expecting instant results without operational changes",
                  "Teams unwilling to integrate AI into existing workflows",
                  "Businesses not ready to invest in operational automation",
                ].map(item => (
                  <div key={item} className="flex items-start gap-3">
                    <span className="text-red-400 flex-shrink-0 mt-0.5 font-bold">✕</span>
                    <p className="text-red-800 text-sm leading-relaxed">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ───────────────────────────────────────────────────────── */}
      <section className="py-24" style={{ background: `linear-gradient(135deg, ${navy} 0%, #1a3260 100%)` }}>
        <div className="max-w-3xl mx-auto px-6 text-center">
          <SectionLabel>Get Started</SectionLabel>
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight">Ready to Put AI to Work in Your Business?</h2>
          <p className="text-gray-300 text-lg mb-10 max-w-xl mx-auto leading-relaxed">
            Book a free 30-minute strategy call. We'll review your operations, identify where AI can add real value, and give you a clear picture of what's possible — with no obligation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={openCalendlyPopup}>Book Your AI Strategy Call</Button>
            <Button variant="secondary" onClick={() => navigate("/resources")}>Get My Free AI Roadmap</Button>
          </div>
        </div>
      </section>

    </Layout>
  );
}